import { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';
import type {
  WProjectCustomizationFullView,
  WProjectFullView,
  WProjectListingCustomizationView,
  WProjectListingView,
} from '@zola/svc-web-api-ts-client';
import { CardType, CARD_TYPE_MAP, isEnvelope } from '../constants/cardTypes';
import {
  isPrimaryCustomizationCardType,
  sortCustomizationTypes,
} from '../constants/customizationTypes';

type CustomizationWithType = { type?: string; medium?: string; name?: string };

// IMPORTANT: This is a hack used during sku migration to get around multiple customizations with the "ENVELOPE" type.
// Don't blame John for that comment, it was ported from web-wedding:  https://github.com/NewAmsterdamLabs/web-wedding/commit/b6274ecea15b28fdf4877007b4e5239814c730fe#diff-77fe5654d7db6163c05af311d6c02d5fdf727e972eb9a61c5fad5ff59c645114R52
export const getTypeFromCustomization = <
  T extends CustomizationWithType | ConvertWebApiDeclaredToActual<CustomizationWithType>
>(
  customization: T
): CardType => {
  switch (customization.type) {
    case 'SAVE_THE_DATE':
      // TODO DSTD
      return customization.medium === 'DIGITAL' ? 'DIGITAL_SAVE_THE_DATE' : customization.type;
    case 'DETAILS':
      return CARD_TYPE_MAP.enclosure;
    case 'ENVELOPE':
      if (/rsvp/i.test(customization.name as string)) {
        return CARD_TYPE_MAP.rsvpEnvelope;
      }
      return CARD_TYPE_MAP.invitationEnvelope;
    default:
      return customization.type as CardType;
  }
};

type CustomizationType = WProjectListingCustomizationView | WProjectCustomizationFullView;

export const isWProjectListingCustomizationView = (
  customization: CustomizationType
): customization is WProjectListingCustomizationView => {
  return !('uuid' in customization);
};

export const isWProjectCustomizationFullView = (
  customization: CustomizationType
): customization is WProjectCustomizationFullView => {
  return 'uuid' in customization;
};

type LeadCustomizationCandidate = { lead?: boolean; type?: string; medium?: string; name?: string };

export const findLeadCustomization = <
  T extends LeadCustomizationCandidate | ConvertWebApiDeclaredToActual<LeadCustomizationCandidate>
>(
  allCustomizations: T[]
): T | undefined => {
  const customizationWithLeadFlag = allCustomizations.find((c) => c.lead);

  if (customizationWithLeadFlag) return customizationWithLeadFlag;

  const firstPrimaryCustomization = allCustomizations.find((c) => {
    const inactive = 'inactive' in c ? c.inactive : false;
    return !inactive && isPrimaryCustomizationCardType(getTypeFromCustomization(c));
  });

  return firstPrimaryCustomization;
};

export function sortCustomizationsAndIdentifyPrimary(
  project: WProjectListingView
): WProjectListingView & {
  primary_customization: WProjectListingCustomizationView | undefined;
};

export function sortCustomizationsAndIdentifyPrimary(
  project: WProjectFullView
): WProjectFullView & {
  primary_customization: WProjectCustomizationFullView | undefined;
};

export function sortCustomizationsAndIdentifyPrimary(
  project: WProjectListingView | WProjectFullView
):
  | (WProjectFullView & { primary_customization: WProjectCustomizationFullView | undefined })
  | (WProjectListingView & {
      primary_customization: WProjectListingCustomizationView | undefined;
    }) {
  return {
    ...project,
    customizations: (project.customizations || []).sort(sortCustomizationTypes),

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - this sometimes infers the wrong type
    primary_customization: findLeadCustomization(project.customizations || []),
  };
}

export const getSelectedEnvelopeType = (customization: CustomizationType): string => {
  if (isWProjectCustomizationFullView(customization)) {
    const optionValues = customization.variation?.option_values || {};
    return isEnvelope(customization.type) ? optionValues['envelope-type'] : '';
  }

  // TODO: where does envelope type come from in the listing view?
  return '';
};

export const getSelectedColor = (customization: CustomizationType) => {
  if (isWProjectCustomizationFullView(customization)) {
    const optionValues = customization.variation?.option_values || {};

    // Colored envelope background color (or use the default)
    return isEnvelope(customization.type) ? `#${optionValues.color}` : null;
  }

  // TODO: where does background color come from in the listing view?
  // page?.background_color
  return null;
};
